import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import LogoHorizontal from "./LogoHorizontal";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalItems, dispatch } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const navLinks = [
    { to: "/",          label: "Home"      },
    { to: "/about",     label: "About"     },
    { to: "/menu",      label: "Menu"      },
    { to: "/order-now", label: "Order"     },
    { to: "/faq",       label: "FAQ"       },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? "bg-white/95 backdrop-blur-sm shadow-sm" : "bg-cream-50/90 backdrop-blur-sm"
    }`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-24">

          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <LogoHorizontal />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`font-body text-base tracking-wide transition-colors ${
                  location.pathname === to
                    ? "text-sage-700 font-bold"
                    : "text-gray-600 hover:text-sage-700"
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <Link to="/order" className="hidden md:inline-flex btn-primary">
              Checkout
            </Link>
            <button
              onClick={() => dispatch({ type: "TOGGLE_CART" })}
              className="relative p-2 text-sage-700 hover:text-sage-900 transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={22} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-dusty-rose text-white text-base font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              className="md:hidden p-2 text-sage-700"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-cream-200 px-4 py-4 space-y-3">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`block font-body text-base py-2 border-b border-cream-100 ${
                location.pathname === to ? "text-sage-700 font-bold" : "text-gray-700"
              }`}
            >
              {label}
            </Link>
          ))}
          <Link to="/order" className="btn-primary block text-center mt-4">
            Checkout
          </Link>
        </div>
      )}
    </nav>
  );
}
